import { computed, Signal } from '@angular/core';
import type { ModuleStructureObject } from '../../api/interfaces';
import {
  calculateMaxPointsForWidget,
  calculateSectionsPoints,
  calculateSectionUsedPoints,
  calculateSubsectionsPoints,
  calculateSubsectionWeights,
  calculateSubsectionWidgets,
  calculateWeightedPointsForWidget,
  calculateWidgetTotalWeights,
} from './points-calculation';

/**
 * The reactive points model over a module structure.
 *
 * The builder and the compiler both need every one of these, derived the same
 * way, but each owns its own structure signal — the builder edits one, the
 * compiler loads one — so this is a factory rather than a service.
 */
export function createPointsModel(
  moduleStructure: Signal<ModuleStructureObject | null>,
) {
  const widgetTotalWeights = computed(() =>
    calculateWidgetTotalWeights(moduleStructure()),
  );
  const subsectionWidgets = computed(() =>
    calculateSubsectionWidgets(moduleStructure()),
  );
  const sectionsPoints = computed(() =>
    calculateSectionsPoints(moduleStructure()),
  );
  const subsectionsPoints = computed(() =>
    calculateSubsectionsPoints(moduleStructure()),
  );
  const sectionUsedPoints = computed(() =>
    calculateSectionUsedPoints(moduleStructure()),
  );
  const subsectionWeights = computed(() =>
    calculateSubsectionWeights(
      subsectionWidgets(),
      widgetTotalWeights(),
      subsectionsPoints(),
    ),
  );

  return {
    widgetTotalWeights,
    subsectionWidgets,
    sectionsPoints,
    subsectionsPoints,
    sectionUsedPoints,
    subsectionWeights,

    /** The points this widget is worth if answered perfectly. */
    maxPointsForWidget: (widgetId: string) =>
      computed(() =>
        calculateMaxPointsForWidget(
          widgetId,
          subsectionWidgets(),
          widgetTotalWeights(),
          subsectionWeights(),
        ),
      ),

    /** The points this widget is worth at the given answer weight. */
    weightedPointsForWidget: (widgetId: string, weight: number | undefined | null) =>
      computed(() =>
        calculateWeightedPointsForWidget(
          widgetId,
          weight,
          subsectionWidgets(),
          subsectionWeights(),
        ),
      ),
  };
}

/** The shape {@link createPointsModel} returns. */
export type PointsModel = ReturnType<typeof createPointsModel>;
